import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { getAmplifyClient } from '@/services/amplifyClient'
import * as queries from '@/services/graphql'
import { ReviewStatus } from '@skop-app/lib-types'
import { duplicateOptionsWithTranslations } from './option.normalized'
import {
  CreateFieldInput,
  OptionCreateInput,
  OptionItem,
  OptionQueryKey,
  OptionUpdateInput,
} from './property.type'
import { fetchFieldItemFn } from './option.query'
import { useCreateField } from './field.mutation'
import { useUpsertTranslation } from '../mutations/translations'
import {
  listQueryKey,
  listQueryPrefix,
  optionsByFieldQueryPrefix,
} from './property.keys'
import { useUserContext } from '@/context'
import { resolveCreationReviewPolicy } from '@/features/reviews'
import {
  beginOptionCacheOperation,
  findCachedOption,
  orderedOptionWrite,
  reconcileOptionCache,
  settleOptionCacheOperation,
} from './option.cache'

export type UpsertOptionVariables = (
  | {
      upsertType: 'create'
      input: OptionCreateInput
      queryKey: OptionQueryKey
    }
  | {
      upsertType: 'update'
      input: OptionUpdateInput
      queryKey: OptionQueryKey
    }
) & { reconciliation?: 'background' | 'await'; intent?: 'unarchive' }

export const useUpsertOption = ({
  canUnarchive = false,
  unarchiveListId,
}: { canUnarchive?: boolean; unarchiveListId?: string } = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['upsertOption'],
    mutationFn: (variables: UpsertOptionVariables) =>
      orderedOptionWrite(
        queryClient,
        `option:${variables.queryKey[1]}:${variables.input.id}`,
        () => executeOptionUpsert(variables),
      ),
    onMutate: async (variables) => {
      const current = findCachedOption(
        queryClient,
        variables.queryKey[1],
        variables.input.id,
      )
      if (
        (variables.intent === 'unarchive' || current?.status === 'ARCHIVED') &&
        variables.input.status === 'ACCEPTED' &&
        (!canUnarchive ||
          !unarchiveListId ||
          variables.queryKey[1] !== unarchiveListId)
      )
        throw new Error('Option unarchive is not authorized')
      // Review and ownership changes can cross the query's existing audience
      // filter. Reconcile those from the server as before; only the lifecycle
      // actions in this lot and ordinary edits can patch every cached audience.
      const canPatch =
        variables.upsertType === 'update' &&
        (variables.input.status === undefined ||
          variables.input.status === 'ARCHIVED' ||
          variables.input.status === 'DELETED' ||
          (variables.input.status === 'ACCEPTED' &&
            (variables.intent === 'unarchive' ||
              current?.status === 'ARCHIVED'))) &&
        (variables.input.organizationID === undefined ||
          variables.input.organizationID === current?.organizationID) &&
        (variables.input.userID === undefined ||
          variables.input.userID === current?.userID) &&
        (variables.input.fieldOptionsId === undefined ||
          variables.input.fieldOptionsId === variables.queryKey[1])
      const operation = await beginOptionCacheOperation(
        queryClient,
        variables.queryKey[1],
        (item) =>
          canPatch && item.id === variables.input.id
            ? { ...item, ...variables.input }
            : item,
      )
      return { operation, canPatch }
    },
    onSettled: async (data, error, variables, cache) => {
      // A full response may include a concurrently changed review status or
      // owner. Confirm only the fields this edit wrote, plus the server time;
      // the scoped background query reconciles the rest.
      const writtenFields = new Set(
        Object.entries(variables.input)
          .filter(([key, value]) => key !== 'id' && value !== undefined)
          .map(([key]) => key),
      )
      writtenFields.add('updatedAt')
      const confirmed = data
        ? Object.fromEntries(
            Object.entries(data).filter(([key]) => writtenFields.has(key)),
          )
        : undefined
      if (cache)
        await settleOptionCacheOperation(
          queryClient,
          cache.operation,
          Boolean(error),
          cache.canPatch && confirmed
            ? (item) =>
                item.id === variables.input.id
                  ? { ...item, ...confirmed }
                  : item
            : undefined,
        )
      const reconciliation = reconcileOptionCache(
        queryClient,
        variables.queryKey[1],
      )
      if (variables.reconciliation !== 'background') return reconciliation
      void reconciliation
    },
  })
}

const getListIdFromQueryKey = (queryKey: OptionQueryKey) => queryKey[1]

export const invalidateOptionQueries = (
  queryClient: Pick<ReturnType<typeof useQueryClient>, 'invalidateQueries'>,
  queryKey: OptionQueryKey,
) => {
  const listId = getListIdFromQueryKey(queryKey)

  // Inactive variants stay invalidated and reload on their next mount.
  // Await both active audiences before reporting mutation completion.
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: listQueryPrefix(listId),
      refetchType: 'active',
    }),
    queryClient.invalidateQueries({
      queryKey: optionsByFieldQueryPrefix(listId),
      refetchType: 'active',
    }),
  ]).then(() => undefined)
}

export const optimisticallySyncOptionQueries = (
  queryClient: Pick<
    ReturnType<typeof useQueryClient>,
    'getQueriesData' | 'setQueryData'
  >,
  variables: UpsertOptionVariables,
) => {
  patchOptionQueries(queryClient, variables)
}

const toOptionItemFromInput = (
  input: OptionCreateInput | OptionUpdateInput,
): OptionItem => ({
  id: input.id,
  label: input.label ?? '',
  value: input.value ?? input.label ?? '',
  status: input.status ?? 'PENDING',
  fieldOptionsId: input.fieldOptionsId ?? undefined,
  optionOptionsId: input.optionOptionsId ?? undefined,
  metadata: input.metadata ?? [],
  userID: input.userID ?? '',
  organizationID: input.organizationID ?? '',
})

export const applyOptionMutationToItems = (
  items: OptionItem[] | undefined,
  variables: UpsertOptionVariables,
): OptionItem[] | undefined => {
  if (!items) return items

  const { input, upsertType } = variables
  const { id } = input
  if (!id) return items

  const applyNestedMutation = (item: OptionItem): OptionItem => {
    if (!item.options) return item

    return {
      ...item,
      options: {
        ...item.options,
        items:
          applyOptionMutationToItems(item.options.items, variables) ??
          item.options.items,
      },
    }
  }

  if (input.status === 'DELETED') {
    return items
      .filter((item) => item.id !== id)
      .map((item) => applyNestedMutation(item))
  }

  if (upsertType === 'update') {
    return items.map((item) =>
      applyNestedMutation(item.id === id ? { ...item, ...input } : item),
    )
  }

  const nextItem = toOptionItemFromInput(input)
  const existingIndex = items.findIndex((item) => item.id === id)
  if (existingIndex >= 0) {
    return items.map((item) =>
      item.id === id ? { ...item, ...nextItem } : item,
    )
  }

  return [...items, nextItem]
}

const patchOptionQueries = (
  client: Pick<
    ReturnType<typeof useQueryClient>,
    'getQueriesData' | 'setQueryData'
  >,
  variables: UpsertOptionVariables,
) => {
  const listId = getListIdFromQueryKey(variables.queryKey)
  const matchingQueries = [
    ...client.getQueriesData<OptionItem[]>({
      queryKey: listQueryPrefix(listId),
    }),
    ...client.getQueriesData<OptionItem[]>({
      queryKey: optionsByFieldQueryPrefix(listId),
    }),
  ]

  for (const [matchedKey, oldData] of matchingQueries) {
    if (!oldData) continue
    const nextData = applyOptionMutationToItems(oldData, variables)
    client.setQueryData<OptionItem[]>(matchedKey, nextData)
  }
}

export const executeOptionUpsert = async (params: UpsertOptionVariables) => {
  const { upsertType, input } = params
  const {
    id,
    label,
    value,
    metadata,
    fieldOptionsId,
    optionOptionsId,
    userID,
    organizationID,
    status,
  } = input

  const listIdFromQueryKey = getListIdFromQueryKey(params.queryKey)
  const isNestedCreate = upsertType === 'create' && optionOptionsId != null
  const normalizedFieldOptionsId = isNestedCreate
    ? (fieldOptionsId ?? listIdFromQueryKey)
    : fieldOptionsId
  const shouldIncludeFieldOptionsId =
    upsertType === 'create'
      ? normalizedFieldOptionsId != null
      : fieldOptionsId !== undefined

  if (
    isNestedCreate &&
    (typeof normalizedFieldOptionsId !== 'string' ||
      normalizedFieldOptionsId.length === 0)
  ) {
    throw new Error('Missing root list id for nested option creation')
  }

  const client = getAmplifyClient()
  const cleanInput = {
    id,
    ...(label !== undefined && { label }),
    ...(userID !== undefined && { userID }),
    ...(organizationID !== undefined && { organizationID }),
    ...(value !== undefined && { value }),
    ...(shouldIncludeFieldOptionsId && {
      fieldOptionsId: normalizedFieldOptionsId,
    }),
    ...(optionOptionsId !== undefined && { optionOptionsId }),
    ...(metadata !== undefined && { metadata }),
    ...(status !== undefined && { status }),
  }

  try {
    const response = await client.graphql({
      query:
        upsertType === 'create'
          ? queries.createOptionMutation
          : queries.updateOptionMutation,
      variables: {
        input: cleanInput,
      },
    })
    const result = response as {
      data?: {
        createOption?: Partial<OptionItem>
        updateOption?: Partial<OptionItem>
      }
    }
    return upsertType === 'create'
      ? result.data?.createOption
      : result.data?.updateOption
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const useDeleteList = () => {
  return useMutation({
    mutationKey: ['deleteList'],
    mutationFn: deleteList,
    onSuccess: (_, _variables, _result, context) => {
      context.client.invalidateQueries({
        queryKey: ['lists-all'],
      })
    },
  })
}

const deleteList = async (params: { id: string; items: string[] }) => {
  const { id, items } = params
  const client = getAmplifyClient()
  try {
    for (const itemId of items) {
      await client.graphql({
        query: queries.updateOptionMutation,
        variables: {
          input: {
            id: itemId,
            status: 'DELETED',
          },
        },
      })
    }

    // Finally, delete the list field itself
    await client.graphql({
      query: queries.deleteFieldMutation,
      variables: {
        input: {
          id,
        },
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

type DuplicateOptionParams = {
  optionId: string
  key: string
}

export const useDuplicateOption = () => {
  const queryClient = useQueryClient()
  const {
    ownership,
    isAdmin,
    isSkopAdmin,
    user,
    organizationScope,
    teamReviewWorkflowPolicy,
  } = useUserContext()
  const activeOrganizationId = organizationScope.activeOrganizationId
  const selectedOrganizationId = organizationScope.selectedOrganizationId

  const { mutateAsync: createField } = useCreateField()
  const { mutateAsync: createOption } = useUpsertOption()
  const { mutateAsync: upsertTranslation } = useUpsertTranslation()

  const {
    mutateAsync: duplicateOption,
    isPending: isDuplicating,
    error,
  } = useMutation({
    mutationKey: ['duplicate-option'],
    mutationFn: async ({ optionId }: DuplicateOptionParams) => {
      const data = await queryClient.ensureQueryData({
        queryKey: listQueryKey(optionId, undefined, 'moderation'),
        queryFn: () =>
          fetchFieldItemFn({
            optionId,
            lang: undefined,
            optionFilter: {
              status: { eq: ReviewStatus.ACCEPTED },
            },
          }),
      })

      const { normalized, supportedKey } = data

      const newFieldId = uuid()
      const targetOrganizationID =
        activeOrganizationId ??
        selectedOrganizationId ??
        process.env.NEXT_PUBLIC_SKOP_ORGID!
      const { elementStatus: status } = ownership
        ? resolveCreationReviewPolicy({
            elementType: 'option',
            source: 'duplicate',
            ownership,
            listOrganizationID: targetOrganizationID,
            isAdmin,
            isSkopAdmin,
            teamReviewWorkflowPolicy,
          })
        : {
            elementStatus: 'ACCEPTED' as const,
          }
      const fieldInput: CreateFieldInput = {
        id: newFieldId,
        organizationFieldsId: targetOrganizationID,
        label: supportedKey.label + ' (copie)',
        value: String(supportedKey.value ?? ''),
        key: supportedKey.key,
        type: 'LIST',
      }

      // Options + traductions dérivées de la structure normalisée
      const { options, translations } = duplicateOptionsWithTranslations({
        normalized,
        newFieldId,
        targetStatus: status,
        targetUserID: user?.id ?? ownership?.byUserID ?? '',
        targetOrganizationID,
      })

      // 1. Créer le nouveau champ
      await createField(fieldInput)

      // 2. Créer options + traductions (et attendre la fin)
      await Promise.all([
        Promise.all(
          options.map((option) =>
            createOption({
              upsertType: 'create',
              input: option,
              queryKey: listQueryKey(newFieldId, undefined, 'picker'),
            }),
          ),
        ),
        Promise.all(
          translations.map((translation) =>
            upsertTranslation({ upsertType: 'create', input: translation }),
          ),
        ),
      ])

      return {
        newFieldId,
        optionsCount: options.length,
        translationsCount: translations.length,
      }
    },
    onSuccess: (_, _variables, _result, context) => {
      context.client.invalidateQueries({
        queryKey: ['lists-all'],
      })
    },
  })

  return {
    duplicateOption,
    isDuplicating,
    error,
  }
}
